import { MELODIES } from '../constants/melodies';
import { MEDITATION_MELODIES } from '../constants/meditationMelodies';

// 音频播放管理类
class AudioManager {
  constructor() {
    this.audioContext = null;
    this.currentAudio = null;
    this.oscillator = null;
    this.gainNode = null;
    this.isPlaying = false;
    this.isUnlocked = false;
    
    // 添加用户交互监听器来解锁音频
    this.setupAudioUnlock();
  }

  // 设置音频解锁
  setupAudioUnlock() {
    const unlockAudio = async () => {
      if (!this.isUnlocked) {
        try {
          await this.initAudioContext();
          // 播放一个静音的音频来解锁
          const source = this.audioContext.createBufferSource();
          const buffer = this.audioContext.createBuffer(1, 1, 22050);
          source.buffer = buffer;
          source.connect(this.audioContext.destination);
          source.start();
          this.isUnlocked = true;
          console.log('Audio unlocked successfully');
          
          // 移除事件监听器
          document.removeEventListener('click', unlockAudio);
          document.removeEventListener('touchstart', unlockAudio);
          document.removeEventListener('keydown', unlockAudio);
        } catch (error) {
          console.error('Failed to unlock audio:', error);
        }
      }
    };

    // 添加多种用户交互事件监听器
    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    document.addEventListener('keydown', unlockAudio);
  }

  // 初始化音频上下文
  async initAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
    }
    
    // 如果音频上下文被暂停，尝试恢复
    if (this.audioContext.state === 'suspended') {
      try {
        await this.audioContext.resume();
        console.log('Audio context resumed successfully');
      } catch (error) {
        console.error('Failed to resume audio context:', error);
      }
    }
    
    return this.audioContext;
  }

  // 播放真实音频文件
  async playAudioFile(audioFile, volume = 0.7, growing = false, loop = true) {
    try {
      console.log('playAudioFile called with:', { audioFile, volume, growing, loop });
      
      // 停止当前播放的音频
      this.stopAll();
      
      // 初始化音频上下文
      await this.initAudioContext();
      
      console.log(`Attempting to load audio file: ${audioFile}`);
      
      // 加载音频文件
      const response = await fetch(audioFile);
      if (!response.ok) {
        throw new Error(`Failed to load audio file: ${audioFile} (${response.status} ${response.statusText})`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      console.log(`Audio file loaded, size: ${arrayBuffer.byteLength} bytes`);
      
      // 检查arrayBuffer是否有效
      if (!arrayBuffer || arrayBuffer.byteLength === 0) {
        throw new Error(`Empty or invalid audio file: ${audioFile}`);
      }
      
      let audioBuffer;
      try {
        audioBuffer = await this.audioContext.decodeAudioData(arrayBuffer);
        console.log(`Audio decoded successfully, duration: ${audioBuffer.duration}s`);
      } catch (decodeError) {
        console.error('Audio decode error:', decodeError);
        console.error('Decode error details:', {
          message: decodeError.message,
          name: decodeError.name,
          audioFile: audioFile
        });
        // 如果解码失败，尝试使用HTML5 Audio作为降级方案
        return this.playWithHTMLAudio(audioFile, volume, growing, loop);
      }
      
      // 创建音频源
      const source = this.audioContext.createBufferSource();
      source.buffer = audioBuffer;
      source.loop = loop; // 根据参数设置是否循环播放
      
      // 创建增益节点用于音量控制
      const gainNode = this.audioContext.createGain();
      source.connect(gainNode);
      gainNode.connect(this.audioContext.destination);
      
      // 设置初始音量
      const initialVolume = growing ? 0.1 : volume;
      gainNode.gain.setValueAtTime(initialVolume, this.audioContext.currentTime);
      console.log('Initial volume set to:', initialVolume);
      
      // 如果启用渐强效果
      if (growing) {
        const growDuration = 3; // 3秒内渐强到目标音量
        gainNode.gain.linearRampToValueAtTime(volume, this.audioContext.currentTime + growDuration);
        console.log('Growing volume enabled, target volume:', volume);
      }
      
      // 播放音频
      source.start();
      
      // 保存当前播放状态
      this.currentAudio = {
        source,
        gainNode,
        startTime: this.audioContext.currentTime
      };
      
      console.log('Web Audio API playback started successfully');
      
      // 返回控制对象
      return {
        stop: () => this.stopAll(),
        setVolume: (newVolume) => this.setVolume(newVolume)
      };
      
    } catch (error) {
      console.error('播放音频文件失败:', error);
      console.error('Error details:', {
        message: error.message,
        name: error.name,
        audioFile: audioFile
      });
      // 尝试使用HTML5 Audio作为降级方案
      try {
        console.log('Trying HTML5 Audio fallback...');
        return this.playWithHTMLAudio(audioFile, volume, growing, loop);
      } catch (fallbackError) {
        console.error('HTML5 Audio fallback also failed:', fallbackError);
        // 最后的降级方案：使用合成音效
        const melodyId = this.extractMelodyIdFromPath(audioFile);
        console.log('Using generated sound as final fallback for:', melodyId);
        return this.playGeneratedSound(melodyId, volume, growing);
      }
    }
  }

  // HTML5 Audio降级方案
  playWithHTMLAudio(audioFile, volume = 0.7, growing = false, loop = true) {
    return new Promise((resolve, reject) => {
      try {
        // 停止当前播放的音频
        this.stopAll();
        
        console.log(`Attempting to play with HTML5 Audio: ${audioFile}`);
        
        const audio = new Audio(audioFile);
        audio.volume = growing ? 0.1 : volume;
        audio.loop = loop;
        
        // 设置事件监听器
        audio.addEventListener('canplaythrough', () => {
          console.log('HTML5 Audio ready to play');
          
          // 如果启用渐强效果
          if (growing) {
            this.applyHTMLAudioGrowingEffect(audio, volume);
          }
          
          audio.play().then(() => {
            console.log('HTML5 Audio playback started successfully');
            
            // 保存当前播放状态
            this.currentAudio = audio;
            
            resolve({
              stop: () => this.stopAll(),
              setVolume: (newVolume) => {
                if (this.currentAudio && this.currentAudio.volume !== undefined) {
                  this.currentAudio.volume = Math.max(0, Math.min(1, newVolume));
                }
              }
            });
          }).catch(playError => {
            console.error('HTML5 Audio play failed:', playError);
            console.error('Play error details:', {
              message: playError.message,
              name: playError.name,
              audioFile: audioFile,
              readyState: audio.readyState,
              networkState: audio.networkState
            });
            reject(playError);
          });
        });
        
        audio.addEventListener('error', (error) => {
          console.error('HTML5 Audio error:', error);
          console.error('Audio error details:', {
            error: error.target?.error,
            code: error.target?.error?.code,
            message: error.target?.error?.message,
            audioFile: audioFile,
            readyState: audio.readyState,
            networkState: audio.networkState
          });
          reject(new Error(`HTML5 Audio failed to load: ${audioFile} (Error code: ${error.target?.error?.code})`));
        });
        
        audio.addEventListener('loadstart', () => {
          console.log(`HTML5 Audio load started: ${audioFile}`);
        });
        
        audio.addEventListener('loadeddata', () => {
          console.log(`HTML5 Audio data loaded: ${audioFile}`);
        });
        
        audio.addEventListener('loadedmetadata', () => {
          console.log(`HTML5 Audio metadata loaded: ${audioFile}, duration: ${audio.duration}s`);
        });
        
        // 开始加载音频
        audio.load();
        
      } catch (error) {
        console.error('HTML5 Audio initialization failed:', error);
        reject(error);
      }
    });
  }

  // HTML5 Audio的渐强效果
  applyHTMLAudioGrowingEffect(audio, targetVolume) {
    const duration = 3000; // 3秒渐强
    const steps = 30;
    const stepDuration = duration / steps;
    const volumeStep = (targetVolume - 0.1) / steps;
    
    let currentStep = 0;
    const interval = setInterval(() => {
      if (!audio || audio.paused || currentStep >= steps) {
        clearInterval(interval);
        return;
      }
      
      audio.volume = Math.min(0.1 + (volumeStep * currentStep), targetVolume);
      currentStep++;
    }, stepDuration);
  }

  // 从音频文件路径中提取melody ID
  extractMelodyIdFromPath(audioFile) {
    // 从路径中提取文件名（不含扩展名）
    const fileName = audioFile.split('/').pop().split('.')[0];
    // 转换为大写并查找对应的melody
    const melodyId = fileName.toUpperCase();
    const melody = MELODIES.find(m => m.id === melodyId);
    return melody ? melodyId : 'CLASSIC'; // 默认返回CLASSIC
  }

  // 播放合成音效（作为备选方案）
  playGeneratedSound(melodyId, volume = 0.5, growing = false) {
    try {
      this.stopAll();

      // 根据melody ID找到对应的配置
      const melody = MELODIES.find(m => m.id === melodyId) || MELODIES[0];
      
      this.initAudioContext();
      this.oscillator = this.audioContext.createOscillator();
      this.gainNode = this.audioContext.createGain();

      this.oscillator.connect(this.gainNode);
      this.gainNode.connect(this.audioContext.destination);

      // 设置音效参数
      this.oscillator.frequency.value = melody.frequency;
      this.oscillator.type = melody.type;

      if (growing) {
        this.gainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime);
        this.gainNode.exponentialRampToValueAtTime(volume, this.audioContext.currentTime + 5);
      } else {
        this.gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
      }

      this.oscillator.start();
      this.isPlaying = true;

      // 创建节拍模式（针对某些音效）
      if (melody.id === 'BUSY' || melody.id === 'BEEP') {
        this.createBeepPattern(melody.frequency);
      }

      return {
        stop: () => this.stopAll(),
        setVolume: (vol) => this.setGeneratedVolume(vol)
      };

    } catch (error) {
      console.error('合成音效播放失败:', error);
      return { stop: () => {}, setVolume: () => {} };
    }
  }

  // 应用渐强效果
  applyGrowingEffect(targetVolume) {
    if (!this.currentAudio) return;

    const duration = 5000; // 5秒渐强
    const steps = 50;
    const stepDuration = duration / steps;
    const volumeStep = (targetVolume - 0.1) / steps;

    let currentStep = 0;
    const interval = setInterval(() => {
      if (!this.currentAudio || currentStep >= steps) {
        clearInterval(interval);
        return;
      }

      this.currentAudio.volume = Math.min(0.1 + (volumeStep * currentStep), targetVolume);
      currentStep++;
    }, stepDuration);
  }

  // 创建节拍模式
  createBeepPattern(baseFrequency) {
    let beepCount = 0;
    const beepInterval = setInterval(() => {
      if (!this.oscillator || !this.isPlaying) {
        clearInterval(beepInterval);
        return;
      }

      // 交替频率创建节拍效果
      if (beepCount % 2 === 0) {
        this.oscillator.frequency.value = baseFrequency;
      } else {
        this.oscillator.frequency.value = baseFrequency * 0.8;
      }
      beepCount++;
    }, 500);

    // 30秒后自动停止
    setTimeout(() => {
      clearInterval(beepInterval);
    }, 30000);
  }

  // 设置音频文件音量
  setVolume(volume) {
    if (this.currentAudio) {
      this.currentAudio.volume = Math.max(0, Math.min(1, volume));
    }
  }

  // 设置合成音效音量
  setGeneratedVolume(volume) {
    if (this.gainNode && this.audioContext) {
      this.gainNode.gain.setValueAtTime(volume, this.audioContext.currentTime);
    }
  }

  // 停止音频文件播放
  stopAudio() {
    if (this.currentAudio) {
      try {
        // 检查是否是Web Audio API对象
        if (this.currentAudio.source) {
          // Web Audio API - 停止source节点
          this.currentAudio.source.stop();
          this.currentAudio.source.disconnect();
          if (this.currentAudio.gainNode) {
            this.currentAudio.gainNode.disconnect();
          }
        } else if (this.currentAudio.pause) {
          // HTML5 Audio对象
          this.currentAudio.pause();
          this.currentAudio.currentTime = 0;
        }
      } catch (error) {
        console.log('停止音频时出错:', error);
      }
      this.currentAudio = null;
    }
    this.isPlaying = false;
  }

  // 停止合成音效
  stopGenerated() {
    if (this.oscillator) {
      try {
        this.oscillator.stop();
        this.oscillator.disconnect();
      } catch (error) {
        console.log('Oscillator already stopped');
      }
      this.oscillator = null;
    }
    if (this.gainNode) {
      try {
        this.gainNode.disconnect();
      } catch (error) {
        console.log('GainNode already disconnected');
      }
      this.gainNode = null;
    }
    // 不要关闭audioContext，因为它可能被其他音频使用
    this.isPlaying = false;
  }

  // 停止所有音频
  stopAll() {
    console.log('stopAll called');
    this.stopAudio();
    this.stopGenerated();
  }
}

// 创建全局音频管理器实例
const audioManager = new AudioManager();

// 导出的主要函数
export async function playAlarmSound(melodyId = 'CLASSIC', volume = 0.7, growing = false, loop = true) {
  try {
    console.log('playAlarmSound called with:', { melodyId, volume, growing, loop });
    
    // 检查音频是否已解锁
    if (!audioManager.isUnlocked) {
      console.warn('Audio not unlocked yet, attempting to unlock...');
      await audioManager.initAudioContext();
    }
    
    // 首先在冥想音频中查找，然后在普通音频中查找
    let melody = MEDITATION_MELODIES.find(m => m.id === melodyId);
    if (!melody) {
      melody = MELODIES.find(m => m.id === melodyId);
    }
    
    if (!melody) {
      throw new Error(`Melody with id ${melodyId} not found`);
    }

    console.log('Found melody:', melody.name, 'Audio file:', melody.audioFile);
    return await audioManager.playAudioFile(melody.audioFile, volume, growing, loop);
  } catch (error) {
    console.error('播放音效失败:', error);
    throw error;
  }
}

export async function playTestSound(melodyId = 'CLASSIC', duration = 2000, volume = 0.3) {
  // 首先在冥想音频中查找，然后在普通音频中查找
  let melody = MEDITATION_MELODIES.find(m => m.id === melodyId);
  if (!melody) {
    melody = MELODIES.find(m => m.id === melodyId) || MELODIES[0];
  }
  
  try {
    console.log('playTestSound called with:', { melodyId, duration, volume });
    
    // 检查音频是否已解锁
    if (!audioManager.isUnlocked) {
      console.warn('Audio not unlocked yet, attempting to unlock...');
      await audioManager.initAudioContext();
    }
    
    // 测试音效播放较短时间
    const audioControl = await audioManager.playAudioFile(melody.audioFile, volume, false);
    
    // 设置自动停止
    setTimeout(() => {
      if (audioControl && audioControl.stop) {
        audioControl.stop();
      }
    }, duration);

    return audioControl;
  } catch (error) {
    console.error('测试音效播放失败:', error);
    return { stop: () => {}, setVolume: () => {} };
  }
}

// 停止所有音频播放
export function stopAllAudio() {
  audioManager.stopAll();
}

// 获取所有可用的音效列表
export function getAvailableMelodies() {
  return MELODIES.map(melody => ({
    id: melody.id,
    name: melody.name,
    description: melody.description
  }));
}

// 预加载音频文件
export function preloadAudioFiles() {
  MELODIES.forEach(melody => {
    const audio = new Audio(melody.audioFile);
    audio.preload = 'metadata';
    audio.addEventListener('canplaythrough', () => {
      console.log(`音频文件已预加载: ${melody.name}`);
    });
    audio.addEventListener('error', () => {
      console.warn(`音频文件加载失败: ${melody.name}, 将使用合成音效`);
    });
  });
}

// 手动解锁音频（用于用户交互时调用）
export async function unlockAudio() {
  try {
    await audioManager.initAudioContext();
    if (!audioManager.isUnlocked) {
      // 播放一个静音的音频来解锁
      const source = audioManager.audioContext.createBufferSource();
      const buffer = audioManager.audioContext.createBuffer(1, 1, 22050);
      source.buffer = buffer;
      source.connect(audioManager.audioContext.destination);
      source.start();
      audioManager.isUnlocked = true;
      console.log('Audio manually unlocked');
    }
    return true;
  } catch (error) {
    console.error('Failed to unlock audio manually:', error);
    return false;
  }
}